import React, { useRef, useState } from 'react';
import { IconButton, Badge, Box, ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { NotificationItemProps } from '../../../../../infrastructure/notification/types';

type NotificationsButtonLayoutProps = {
  visibleItems: NotificationItemProps[];
  responses: Record<string, any>;
  totalCount: number;
};

const POPPER_SX = {
  zIndex: 1200,
} as const;

const PAPER_SX = {
  mt: 1,
  width: 360,
  overflow: 'visible',
  position: 'relative',
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--mui-palette-border-default)',
  backgroundColor: 'var(--mui-palette-background-paper)',
  px: 1.5,
  py: 1.5,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    right: 12,
    width: 14,
    height: 14,
    transform: 'rotate(45deg)',
    backgroundColor: 'var(--mui-palette-background-paper)',
    borderTop: '1px solid var(--mui-palette-border-default)',
    borderLeft: '1px solid var(--mui-palette-border-default)',
    borderTopLeftRadius: 3,
  },
} as const;

const SCROLL_AREA_SX = {
  maxHeight: 320,
  overflowY: 'auto',
  overflowX: 'hidden',

  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
  overscrollBehavior: 'contain',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
} as const;

const EMPTY_STATE_SX = {
  minHeight: 132,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  color: 'text.secondary',
  textAlign: 'center',
} as const;

const EMPTY_TEXT_SX = {
  fontSize: 13,
} as const;

const NotificationsButtonLayout = ({ visibleItems, responses, totalCount }: NotificationsButtonLayoutProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const hasNotifications = totalCount > 0;

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) return;
    setOpen(false);
  };

  return (
    <>
      <IconButton ref={anchorRef} onClick={handleToggle}>
        <Badge
          badgeContent={totalCount}
          max={99}
          color="error"
          overlap="circular"
          invisible={!hasNotifications}
          sx={{
            '& .MuiBadge-badge': {
              height: 16,
              minWidth: 16,
              padding: '0 4px',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1,
            },
          }}
        >
          <NotificationsNoneIcon fontSize="medium" color="action" />
        </Badge>
      </IconButton>

      <Popper open={open} anchorEl={anchorRef.current} sx={POPPER_SX} placement="bottom-end" transition>
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
              <Paper elevation={6} sx={PAPER_SX}>
                <Box sx={SCROLL_AREA_SX}>
                  {visibleItems.length > 0 ? (
                    visibleItems.map(item => (
                      <Box key={item.type} onClick={() => setOpen(false)}>
                        {item.component(responses[item.type])}
                      </Box>
                    ))
                  ) : (
                    <Box sx={EMPTY_STATE_SX}>
                      <MarkChatReadIcon sx={{ fontSize: 20 }} color="action" />
                      <Box sx={EMPTY_TEXT_SX}>No new notifications</Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default NotificationsButtonLayout;
